import * as toast from '@weblite/react-toastify'
import { Box, Typography } from '@mui/material'
import { ApproveIcon, RepeatIcon } from '@weblite/icons'
import { LIMITS } from '@weblite/permission'
import { toLocale } from '$func/text'
import { getUser, getUserDisplayName } from '$logic/users'
import { getRoom } from '$logic/room'
import { getHall } from '$logic/hall'
import {
  ClearHistoryIcon,
  RemoveIcon,
  CopyIcon,
  ExclamationMarkIcon,
  AdministratorIcon,
  ForwardIcon,
  UnpinIcon,
  PinMessage,
} from '$common/icons'
import { t } from '$i18n'
import './index.css'
import { Palette } from '$setup/material-theme'
import { MAX_FILE_MESSAGE_SIZE_MB } from '$logic/message/helper'

const options = { position: 'bottom-center', className: 'ft-14 mb-8' } as const

export function ToastProvider() {
  return (
    <toast.ToastContainerWrapper
      style={{ minHeight: '48px', bottom: '55px' }}
      rtl
      newestOnTop
    />
  )
}

const getIcon = (type: 'success' | 'error' | 'info') => {
  if (type === 'success')
    return <ApproveIcon sx={{ color: Palette.neutral000 }} />
  if (type === 'error') return <toast.ErrorIcon />
  if (type === 'info') return <ApproveIcon sx={{ color: Palette.neutral000 }} />
  return <ApproveIcon sx={{ color: Palette.neutral000 }} />
}

export const showToast = (
  message: string,
  type: 'success' | 'error' | 'info',
) => {
  toast[type](message, {
    ...options,
    icon: getIcon(type),
  })
}

export const showErrorSnackbar = (content: string, toastId?: string) => {
  analite('ERROR_TOAST', { msg: content })

  toast.error(content, {
    ...options,
    icon: <toast.ErrorIcon />,
    toastId,
  })
}

export const showConnectionProblemSnackbar = (
  operationAction: () => unknown,
) => {
  toast.action(
    t('snackbar.connectionProblem.text'),
    t('common.retry'),
    operationAction,
    options,
  )
}

export const showNewUpdateIsAvailableSnackbar = (
  operationAction: () => unknown,
) => {
  toast.action(
    t('snackbar.newUpdateIsAvailable.text'),
    t('snackbar.newUpdateIsAvailable.actionText'),
    operationAction,
    options,
  )
}

export const showInstallPWAPromptSnackbar = (
  operationAction: () => unknown,
) => {
  toast.action(
    t('snackbar.installPWAPrompt.text'),
    t('snackbar.installPWAPrompt.actionText'),
    operationAction,
    options,
  )
}

export const showGeneratingImageFailedSnackbar = () => {
  toast.error(t('snackbar.genImageFailed.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showGeneratingTextFailedSnackbar = () => {
  toast.error(t('snackbar.genTextFailed.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showGettingOwnerWorkspaceFailedSnackbar = () => {
  toast.error(t('snackbar.failedToLoadUserWorkspaces'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showOperationFailedSnackbar = (operationAction: () => unknown) => {
  toast.action(
    t('snackbar.operationFailed.text'),
    t('common.retry'),
    operationAction,
    options,
  )
}

export const showNetworkErrorSnackbar = () => {
  toast.error(t('serverError'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}
export const showMaxCharacterInTextReachedErrorSnackbar = () => {
  toast.error(t('snackbar.maxCharacterInTextMessageReached'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}
export const showMaxCharacterInCaptionReachedErrorSnackbar = () => {
  toast.error(t('snackbar.maxCharacterInCaptionReached'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}
export const showNoPermissionToSendWappErrorSnackbar = () => {
  toast.error(t('hallPermissionsPage.common.noPermissionToSendWapp'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showUsernameIsInvalidErrorSnackbar = () => {
  toast.error(t('snackbar.usernameIsInvalid'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showThereIsAProblemInConnectionSnackbar = () => {
  toast.error(t('snackbar.thereIsAProblemInConnection.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showInvalidLinkSnackbar = () => {
  toast.error(t('snackbar.invalidLink.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showCouldntJoinClassSnackbar = () => {
  toast.error(t('snackbar.couldntJoinClass'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showSelectionLimitSnackbar = (num: number) => {
  toast.error(t('snackbar.selectionLimit.text')(num), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}
export const showForwardMenuPermissionSnackbar = () => {
  toast.error(t('snackbar.noForwardPermission.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showIdentifierDoesNotExistSnackbar = () => {
  toast.error(t('snackbar.identifierNotExist.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showPinRoomLimitSnackbar = () => {
  toast.error(t('snackbar.pinRoomLimit.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showNoPermissoinToSendMessageSnackbar = () => {
  toast.error(t('snackbar.noPermissionToSend.text'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showHallAdminsLimitSnackbar = () => {
  toast.error(
    t('snackbar.maximumNumberOfHallAdminsExceeded')(LIMITS.hallAdminsCount),
    {
      ...options,
      icon: <toast.ErrorIcon />,
    },
  )
}

export const showInviteLinkExpiredSnackbar = () => {
  toast.info(t('snackbar.inviteLinkExpired.text'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUnableToShowContextMenuSnackbar = (isGroup: boolean) => {
  toast.info(t('contextMenu.limitedByAdmin')(isGroup), {
    position: 'top-center',
    className: 'ft-14 m-16',
  })
}

export const showNMemberRemovedFromHallSnackbar = async (
  count: number,
  hallId: string,
) => {
  const { type } = (await getRoom(hallId)) || {}

  toast.info(
    t('snackbar.nMemberRemovedFromHall.text')(
      toLocale(count),
      type === 'GROUP' ? t('common.group') : t('common.channel'),
    ),
    {
      ...options,
      icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
    },
  )
}

export const showCanNotAccessViaForwardedMessageSnackbar = () => {
  toast.info(t('snackbar.canNotAccessViaForwardedMessage.text'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showHistoryWasClearedSnackbar = () => {
  toast.info(t('snackbar.historyWasCleared.text'), {
    ...options,
    icon: <ClearHistoryIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showChatRemovedSnackbar = () => {
  toast.info(t('snackbar.chatRemoved.text'), {
    ...options,
    icon: <RemoveIcon sx={{ color: Palette.neutral000 }} />,
  })
}
export const showChatsRemovedSnackbar = () => {
  toast.info(t('snackbar.chatsRemoved.text'), {
    ...options,
    icon: <RemoveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showTextSavedInMemorySnackbar = () => {
  toast.info(t('snackbar.textSavedInMemory.text'), {
    ...options,
    icon: <CopyIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showLinkSavedInMemorySnackbar = () => {
  toast.info(t('snackbar.linkSavedInMemory.text'), {
    ...options,
    icon: <CopyIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showHashtagSavedInMemorySnackbar = () => {
  toast.info(t('snackbar.hashtagSavedInMemory.text'), {
    ...options,
    icon: <CopyIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showPhoneNumberSavedInMemorySnackbar = () => {
  toast.info(t('snackbar.phoneNumberSavedInMemory.text'), {
    ...options,
    icon: <CopyIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showBioSavedInMemorySnackbar = () => {
  toast.info(t('snackbar.bioSavedInMemory.text'), {
    ...options,
    icon: <CopyIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showCallEditedSnackbar = (type: string) => {
  toast.info(t('snackbar.call.edited')(type), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showCallDeletedSnackbar = (type: string) => {
  toast.info(t('snackbar.call.deleted')(type), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUsernameSavedInMemorySnackbar = () => {
  toast.info(t('snackbar.usernameSavedInMemory.text'), {
    ...options,
    icon: <CopyIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showReportSendSnackbar = () => {
  toast.info(t('snackbar.reportSent.text'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUserBannedSnackbar = () => {
  toast.info(t('snackbar.userBanned.text'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUserUnbannedSnackbar = () => {
  toast.info(t('snackbar.userUnbanned.text'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUploadingImageSnackbar = () => {
  toast.info(t('snackbar.uploadingImage.text'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUserRemovedFromYourContactSnackbar = async (
  userId: string,
) => {
  const user = await getUser(userId)
  const fullName = getUserDisplayName(user)

  toast.info(
    `${fullName} ${t('snackbar.userRemovedFromYourContact.text.postfix')}`,
    {
      ...options,
      icon: <ExclamationMarkIcon sx={{ color: Palette.neutral000 }} />,
    },
  )
}

export const showUserPromotedToAdminSnackbar = async (userId: string) => {
  const user = await getUser(userId)
  const fullName = getUserDisplayName(user)

  toast.info(`${fullName} ${t('snackbar.userPromotedToAdmin.text.postfix')}`, {
    ...options,
    icon: <AdministratorIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showUserRemovedFromRoomSnackbar = async (
  userId: string,
  hallId: string,
) => {
  const user = await getUser(userId)
  const fullName = getUserDisplayName(user)
  const { name: hallName } = await getHall(hallId)

  toast.info(
    // TODO: acceptance of the JSX element in React Toastify
    `${fullName}  ${t(
      'snackbar.userRemovedFromRoom.text.postfix.prefix',
    )} ${hallName} ${t('snackbar.userPromotedToAdmin.text.postfix')}`,
    {
      ...options,
      icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
    },
  )
}

export const showNMessageForwardedToChatSnackbar = (
  count: number,
  multipleMessage = false,
) => {
  toast.info(
    t('snackbar.nMessageForwardedToChat.text')(
      multipleMessage,
      toLocale(count),
    ),
    {
      ...options,
      icon: <ForwardIcon sx={{ color: Palette.neutral000 }} />,
    },
  )
}

export const showMessagesSentSuccessfullySnackbar = () => {
  toast.info(t('snackbar.sharedSuccessfully'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showMessagePinnedSnackbar = () =>
  toast.info(t('snackbar.pinnedSuccessfully'), {
    ...options,
    icon: <PinMessage sxProps={{ color: Palette.neutral000 }} />,
  })

export const showMessageUnPinnedSnackbar = () => {
  toast.info(t('snackbar.unPinnedSuccessfully'), {
    ...options,
    icon: <UnpinIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showMessagesNotExistSnackbar = () => {
  toast.info(t('snackbar.messageNotExist'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showClearSyncedContactsSnackbar = () => {
  toast.info(t('snackbar.syncedContactsClearedSuccessfully'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showSuccessSnackbar = (text: string) => {
  toast.info(text, {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showFileMessageLimitErrorSnackBar = async () => {
  toast.error(
    t('snackbar.fileMessageSizeLimitExceeded')(MAX_FILE_MESSAGE_SIZE_MB),
    { ...options },
  )
}

export const showReloadingContactsInProgressSnackbar = () => {
  toast.info(t('snackbar.reloadingContactsInProgress'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showContactsReloadedSuccessfullySnackbar = () => {
  toast.info(t('snackbar.ContactsReloadedSuccessfully'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}
export const showInviteLinkSentSuccessfullySnackbar = () => {
  toast.info(t('snackbar.inviteLinkSentSuccessfully'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showInvalidStoryFileTypeSnackbar = () => {
  toast.error(t('snackbar.invalidStoryFileType'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showCallEndedSnackbar = (
  reason: string,
  reasonType?: 'error' | 'info',
) => {
  if (reasonType === 'error') showErrorSnackbar(reason)
  else toast.info(reason, options)
}

export const showForbiddenToInviteSnackbar = () => {
  toast.error(t('snackbar.invite.error.FORBIDDEN'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showExceedsCapacityToInviteSnackbar = () => {
  toast.error(t('snackbar.invite.error.EXCEEDS_CAPACITY'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}
export const showNoOneInvitedErrorSnackbar = () => {
  toast.error(t('snackbar.invite.noOneInvited'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showSuccessfullyInvitedSnackbar = (
  invited: string,
  registered: string,
) => {
  toast.info(t('snackbar.invite.successfully')(invited, registered), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showJoinRequestSentSuccessfullySnackbar = () => {
  toast.info(t('snackbar.joinRequestSentSuccessfully'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showYouHaveAlreadyRequestedToJoinSnackbar = () => {
  toast.error(t('snackbar.youHaveAlreadyRequestedToJoin'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showJoinRequestAcceptedSnackbar = (userName: string) => {
  toast.info(t('snackbar.joinRequestAccepted')(userName), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}

export const showJoinRequestRejectedSnackbar = (
  userName: string,
  // onCancel: () => unknown,
) => {
  toast.info(t('snackbar.joinRequestRejected')(userName), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
  // toast.action(
  //   t('snackbar.joinRequestRejected')(userName),
  //   <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
  //     <RepeatIcon sx={{ color: 'primary300', width: '20px', height: '20px' }} />
  //     <Typography color="primary300">{t('common.revert')}</Typography>
  //   </Box>,
  //   onCancel,
  //   {
  //     ...options,
  //     hideProgressBar: false,
  //     icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  //     autoClose: 5000,
  //   },
  // )
}

export const showFailedToAcceptJoinRequestSnackbar = (userName: string) => {
  toast.error(t('snackbar.failedToAcceptJoinRequest')(userName), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showFailedToRejectJoinRequestSnackbar = (userName: string) => {
  toast.error(t('snackbar.failedToRejectJoinRequest')(userName), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showInfoSuccessfullyUpdatedSnackbar = () => {
  toast.info(t('snackbar.infoSuccessfullyUpdated'), {
    ...options,
  })
}

export const showSignupEndedSuccessfullySnackbar = () => {
  toast.info(t('snackbar.signupEndedSuccessfully'), {
    ...options,
  })
}

export const showSwitchAutoSyncActivationSnackbar = (activated: boolean) => {
  toast.info(t('snackbar.switchSyncContactActivation')(activated))
}

export const showNoOneInvitedSuccessfullySnackbar = () => {
  toast.error(t('snackbar.invite.noOneInvitedSuccessfully'), {
    ...options,
    icon: <toast.ErrorIcon />,
  })
}

export const showTasksLimitSnackbar = () => {
  toast.info(t('snackbar.specialitiesLimit'), {
    ...options,
  })
}

export const showIncomingCallAcceptedFromAnotherDeviceSnackbar = () => {
  toast.info(t('snackbar.incomingCallAcceptedFromAnotherDevice'), {
    ...options,
    icon: <ApproveIcon sx={{ color: Palette.neutral000 }} />,
  })
}


export const showCallRoomIsFullSnackbar = (callType: string) => {
  toast.error(t('snackbar.callRoomIsFull')(callType), {
    ...options,
    autoClose: 3000,
  })
}

export const showWorkspaceCreatedSuccessfully = () => {
  toast.info(t('snackbar.workspaceCreated'))
}

export const showWorkspaceUpdatedSuccessfully = () => {
  toast.info(t('snackbar.workspaceUpdated'))
}

export const showWorkspaceDeletedSuccessfully = () => {
  toast.info(t('snackbar.workspaceDeleted'))
}

export const showHallsMovedToWorkspaceSuccessfullySnackbar = () => {
  toast.info(t('snackbar.hallsMovedToWorkspace'))
}

export const showHallsRemovedFromWorkspaceSuccessfullySnackbar = () => {
  toast.info(t('snackbar.hallsRemovedFromWorkspace'))
}

export const showSystemSnackbar = ({ type }: { type: string }) => {
  if (type === 'serverError') showNetworkErrorSnackbar()
}
